import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { TenantModule, TenantType } from '../../services/tenant-features.service';
import {
  TENANT_MODULE_OPTIONS,
  TENANT_TYPE_OPTIONS,
  TenantTypeOption,
  sortModules,
} from '../../services/tenant-modules';

let nextPickerId = 0;

/**
 * Organisation type cards + module switches. Picking a type presets its modules (minus any the
 * plan blocks); each module can then be switched on or off. Purely presentational: the parent
 * owns the values and saves them.
 *
 * Standalone so the settings page (SettingsModule) and the platform tenant screens (AppModule)
 * can both use it.
 */
@Component({
  selector: 'app-tenant-modules-picker',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './tenant-modules-picker.component.html',
  styleUrl: './tenant-modules-picker.component.scss',
})
export class TenantModulesPickerComponent {
  @Input() type: TenantType = TenantType.Mixed;
  @Input() modules: TenantModule[] = [];
  /** Modules the plan switches off: shown, but locked. */
  @Input() blocked: TenantModule[] = [];
  /** Denser layout for dialogs (no type descriptions, two columns). */
  @Input() compact = false;
  @Input() disabled = false;

  @Output() typeChange = new EventEmitter<TenantType>();
  @Output() modulesChange = new EventEmitter<TenantModule[]>();

  readonly typeOptions = TENANT_TYPE_OPTIONS;
  readonly moduleOptions = TENANT_MODULE_OPTIONS;
  private readonly uid = `tm-${++nextPickerId}`;
  /** id of the "Organisation type" label (aria-labelledby of the radio group). */
  readonly typesLabelId = `${this.uid}-types`;

  idFor(module: TenantModule): string {
    return `${this.uid}-${module}`;
  }

  isOn(module: TenantModule): boolean {
    return this.modules.includes(module) && !this.isBlocked(module);
  }

  isBlocked(module: TenantModule): boolean {
    return this.blocked.includes(module);
  }

  moduleLabelKey(module: TenantModule): string {
    return this.moduleOptions.find((o) => o.module === module)?.labelKey ?? module;
  }

  selectType(option: TenantTypeOption): void {
    if (this.disabled) return;
    this.type = option.type;
    this.typeChange.emit(option.type);
    this.emitModules(option.modules.filter((m) => !this.isBlocked(m)));
  }

  toggle(module: TenantModule, event: Event): void {
    const on = (event.target as HTMLInputElement).checked;
    const next = new Set(this.modules);
    if (on) next.add(module);
    else next.delete(module);
    this.emitModules([...next]);
  }

  private emitModules(list: Iterable<string>): void {
    this.modules = sortModules(list);
    this.modulesChange.emit(this.modules);
  }
}
